/* eslint-disable camelcase */
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';

export const clearCategory = (id, items, setItems) => {
  // item in booking.request.items:
  // category_id,
  // item_token,
  // item_id,
  // name,
  // quantity,
  const updated = items?.filter(
    (item) => item.category_id !== id
  );

  setItems(updated);
};

export const getNumItemsSelectedInCategory = (id, items) => items?.reduce(
  (iter, item) => {
    if (item.category_id === id) {
      return (iter + item.quantity);
    }
    return iter;
  },
  0
);

export const getOptionGroup = (menu, id) => menu?.option_groups?.find(
  (g) => g.option_group_id === id
);

export const getAddOn = (menu, id, categoryId) => getOptionGroup(menu, categoryId)?.options?.find(
  (opt) => opt.item_id === id
);

export const withMultiplier = (multiplier, val) => {
  if (!Number.isInteger(+multiplier)) {
    return val;
  }

  return multiplier * val;
};

export const isFulfilledCategory = (category, items, multiplier = 1) => {
  const { min_quantity, option_group_id, select_per_guest } = category;
  if (!Number.isInteger(min_quantity)) {
    return undefined;
  }
  const numSelectedInCategory = (
    items
      .filter((item) => item.category_id === option_group_id)
      .reduce(
        (iter, item) => iter + item.quantity,
        0
      )
  );
  // select_per_guest acts as a multiplier. min * the number of guests
  // otherwise, it's per booking (multiplier of 1)
  return numSelectedInCategory >= (min_quantity * (select_per_guest ? multiplier : 1));
};

export const isMaxSelectedCategory = (category, items, multiplier = 1) => {
  const { max_quantity, option_group_id, select_per_guest } = category;

  if (typeof max_quantity !== 'number') {
    return false;
  }

  const numSelectedInCategory = (
    items
      .filter((item) => item.category_id === option_group_id)
      .reduce(
        (iter, item) => iter + item.quantity,
        0
      )
  );

  return numSelectedInCategory >= (max_quantity * (select_per_guest ? multiplier : 1));
};

export const numItemsToMaxInCategory = (category, items, multiplier = 1) => {
  const {
    max_quantity, min_quantity, option_group_id, select_per_guest,
  } = category;

  if (typeof max_quantity !== 'number' && typeof min_quantity !== 'number') {
    return undefined;
  }

  const numSelectedInCategory = (
    items
      .filter((item) => item.category_id === option_group_id)
      .reduce(
        (iter, item) => iter + item.quantity,
        0
      )
  );

  const int = ((min_quantity || max_quantity) * (select_per_guest ? multiplier : 1)) - numSelectedInCategory;
  return (int < 0) ? 0 : int;
};

export const getFirstUnfulfilledCategory = (menu, items, multiplier = 1) => {
  const categories = menu.option_groups;

  return categories.find(
    (category) => (isFulfilledCategory(category, items, multiplier) === false)
  );
};

export const validateCheckout = (menuWithQuantities) => {
  const categories = menuWithQuantities?.option_groups;
  if (!categories) {
    return true;
  }
  const addOnsWithQuantity = flatten(categories.map((category) => category.options));
  const r = addOnsWithQuantity.reduce(
    (bool, val) => {
      const q = val.quantity;
      const i = val.available_inventory?.quantity;
      if (q === undefined) {
        return bool;
      }
      if (i === null || i === undefined) {
        return bool;
      }
      return bool && q <= i;
    },
    true
  );
  return r;
};

// category_id
// item_token
// item_id
// name
// quantity
export const getItemForBooking = ({
  menu, id, categoryId, quantity,
}) => {
  const item = getAddOn(menu, +id, +categoryId);

  /* eslint-disable-next-line camelcase */
  const { item_token, item_id, name } = item;
  return {
    category_id: categoryId,
    item_token,
    item_id,
    name,
    quantity,
  };
};

/* take struct_items from booking and update or add any new item with quantitty */
export const getUpdatedItemsForBooking = ({
  menu, structItems, itemId, categoryId, quantity,
}) => {
  // update the item by finding it or updating it
  // or by adding a new item
  const updated = structItems.reduce(
    (iter, i) => {
      // match by id and category
      if (i.item_id === +itemId && i.category_id === +categoryId) {
        const updatedItem = { ...i, quantity };
        iter.push(updatedItem);
      } else {
        iter.push({ ...i });
      }
      return iter;
    },
    []
  );

  // get the item and update it with the relevant info for booking
  if (!updated.find((i) => (i.item_id === +itemId && i.category_id === +categoryId))) {
    updated.push(
      getItemForBooking({
        menu,
        id: +itemId,
        categoryId: +categoryId,
        quantity,
      })
    );
  }

  // and filter out any that have a quantity of 0
  return updated.filter((i) => i.quantity !== 0);
};

export const getAddOnsWithQuantity = (menu, structItems) => {
  const result = cloneDeep(menu);
  if (structItems) {
    structItems.forEach(
      (val) => {
        const addOn = getAddOn(result, val.item_id, val.category_id);
        if (addOn) {
          addOn.quantity = val.quantity || 0;
        }
      }
    );
  }

  return result;
};

export const updateBookingInfoWithAddOnsTokens = (items, setItems, menu) => {
  if (!items) {
    return;
  }
  const updated = items.reduce(
    (iter, i) => {
      const addOn = getAddOn(menu, i.item_id, i.category_id);
      if (addOn) {
        iter.push(
          {

            ...i,
            item_token: addOn.item_token,
          }
        );
        return iter;
      }
      iter.push(i);
      return iter;
    },
    []
  );
  setItems(updated);
};

export const updateBookingInfoForSoldOut = (items, setItems, menu) => {
  if (!items) {
    return;
  }
  const updated = items.reduce(
    (iter, i) => {
      const addOn = getAddOn(menu, i.item_id, i.category_id);
      if (addOn && addOn?.available_inventory?.quantity === 0) {
        const newItem = { ...i, quantity: 0 };
        iter.push(newItem);
        return iter;
      }
      iter.push(i);
      return iter;
    },
    []
  );
  setItems(updated);
};
